import { useEffect, useMemo, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import { ArrowLeft, Minus, Plus, ShoppingBag, ShieldCheck, Truck, Store, Phone } from "lucide-react";
import { useCart, inr } from "../context/CartContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ProductDetail() {
  const { productId } = useParams();
  const { add } = useCart();
  const [product, setProduct] = useState(null);
  const [related, setRelated] = useState([]);
  const [active, setActive] = useState(0);
  const [qty, setQtyState] = useState(1);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    setProduct(null);
    setActive(0);
    setQtyState(1);
    setNotFound(false);
    window.scrollTo(0, 0);
    axios
      .get(`${API}/products/${productId}`)
      .then((r) => {
        setProduct(r.data);
        return axios.get(`${API}/products`, { params: { category: r.data.category } });
      })
      .then((r) => setRelated(r.data.filter((p) => p.id !== productId).slice(0, 4)))
      .catch(() => setNotFound(true));
  }, [productId]);

  const images = useMemo(() => {
    if (!product) return [];
    const list = product.images?.length ? product.images : [product.image];
    return [...new Set([product.image, ...list])];
  }, [product]);

  const handleAdd = () => {
    add(product, qty);
    toast.success(`${qty} × ${product.name} added to your bag`);
  };

  return (
    <div className="min-h-screen bg-[#FBF9F6]" data-testid="product-detail-page">
      <header className="bg-[#2D1B36] text-[#FBF9F6]">
        <div className="max-w-[1200px] mx-auto px-6 py-6 flex items-center justify-between">
          <Link to="/" data-testid="detail-logo" className="font-serif-d text-2xl">
            Innus <span className="italic text-[#C5A059]">Lace Centre</span>
          </Link>
          <Link
            to="/account"
            data-testid="detail-account-link"
            className="font-accent text-[10px] tracking-[0.25em] uppercase text-[#FBF9F6]/60 hover:text-[#DFCA9B] transition-colors"
          >
            My Account
          </Link>
        </div>
      </header>

      <main className="max-w-[1200px] mx-auto px-6 py-12 md:py-20">
        <Link
          to="/#shop"
          data-testid="detail-back-link"
          className="inline-flex items-center gap-2 font-accent text-[10px] tracking-[0.25em] uppercase text-[#8A818F] hover:text-[#C5A059] transition-colors mb-10"
        >
          <ArrowLeft size={13} /> Back to shop
        </Link>

        {notFound ? (
          <div className="py-24 text-center" data-testid="detail-not-found">
            <p className="font-serif-d italic text-3xl text-[#8A818F]">We couldn't find that product.</p>
            <Link to="/" className="mt-6 inline-block font-accent text-xs tracking-[0.25em] uppercase text-[#4A2B58] hover:text-[#C5A059] transition-colors">
              Browse the collection
            </Link>
          </div>
        ) : !product ? (
          <p className="py-24 text-center font-serif-d italic text-2xl text-[#8A818F]" data-testid="detail-loading">
            Unrolling the shelf…
          </p>
        ) : (
          <>
            <div className="grid md:grid-cols-2 gap-12 lg:gap-20">
              <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }}>
                <div className={`relative overflow-hidden bg-[#F2EFEB] aspect-[3/4] ${product.featured ? "arch-frame" : ""}`}>
                  <AnimatePresence mode="wait">
                    <motion.img
                      key={images[active]}
                      src={images[active]}
                      alt={product.name}
                      initial={{ opacity: 0, scale: 1.02 }}
                      animate={{ opacity: 1, scale: 1 }}
                      exit={{ opacity: 0 }}
                      transition={{ duration: 0.4 }}
                      className="absolute inset-0 w-full h-full object-cover"
                      data-testid="detail-image-main"
                    />
                  </AnimatePresence>
                </div>
                {images.length > 1 && (
                  <div className="mt-4 flex gap-3 overflow-x-auto pb-1" data-testid="detail-thumbnails">
                    {images.map((img, i) => (
                      <button
                        key={img}
                        onClick={() => setActive(i)}
                        data-testid={`detail-thumb-${i}`}
                        className={`shrink-0 w-20 h-24 overflow-hidden border transition-colors duration-300 ${
                          active === i ? "border-[#C5A059]" : "border-[#E8E4DF] opacity-60 hover:opacity-100"
                        }`}
                      >
                        <img src={img} alt="" className="w-full h-full object-cover" />
                      </button>
                    ))}
                  </div>
                )}
              </motion.div>

              <motion.div
                initial={{ opacity: 0, y: 24 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.7, delay: 0.15 }}
                className="flex flex-col justify-center"
              >
                <p className="font-accent text-[11px] tracking-[0.35em] uppercase text-[#C5A059]" data-testid="detail-category">
                  {product.category}
                </p>
                <h1 className="mt-4 font-serif-d font-light text-4xl md:text-5xl tracking-tight text-[#2D1B36] leading-tight" data-testid="detail-name">
                  {product.name}
                </h1>
                <p className="mt-6 text-base md:text-lg leading-relaxed text-[#514357]" data-testid="detail-description">
                  {product.description}
                </p>
                <div className="mt-8 flex items-baseline gap-3">
                  <span className="font-serif-d text-4xl text-[#4A2B58]" data-testid="detail-price">{inr(product.price)}</span>
                  <span className="text-sm text-[#8A818F]">{product.unit}</span>
                </div>

                <div className="mt-10 flex flex-wrap items-center gap-4">
                  <div className="inline-flex items-center border border-[#E8E4DF]">
                    <button
                      onClick={() => setQtyState(Math.max(1, qty - 1))}
                      data-testid="detail-qty-minus"
                      className="px-4 py-3.5 text-[#4A2B58] hover:bg-[#F2EFEB] transition-colors"
                    >
                      <Minus size={14} />
                    </button>
                    <span className="px-5 font-accent text-base" data-testid="detail-qty">{qty}</span>
                    <button
                      onClick={() => setQtyState(qty + 1)}
                      data-testid="detail-qty-plus"
                      className="px-4 py-3.5 text-[#4A2B58] hover:bg-[#F2EFEB] transition-colors"
                    >
                      <Plus size={14} />
                    </button>
                  </div>
                  <button
                    onClick={handleAdd}
                    data-testid="detail-add-button"
                    className="flex-1 min-w-[200px] flex items-center justify-center gap-2 bg-[#4A2B58] text-[#FBF9F6] font-accent text-xs tracking-[0.25em] uppercase py-4 hover:bg-[#2D1B36] transition-colors duration-300"
                  >
                    <ShoppingBag size={15} /> Add to Bag — {inr(product.price * qty)}
                  </button>
                </div>

                <a
                  href="tel:+917356572909"
                  data-testid="detail-call-link"
                  className="mt-5 inline-flex items-center gap-2 text-sm text-[#2E6669] hover:text-[#C5A059] transition-colors"
                >
                  <Phone size={14} strokeWidth={1.5} /> Questions? Call the shop — 73565 72909
                </a>

                <div className="mt-10 pt-8 border-t border-[#E8E4DF] grid grid-cols-3 gap-4">
                  {[
                    { icon: ShieldCheck, label: "Secure online payment" },
                    { icon: Truck, label: "Home delivery in N. Paravur" },
                    { icon: Store, label: "Or pick up in store" },
                  ].map((t) => (
                    <div key={t.label} className="text-center">
                      <t.icon size={18} strokeWidth={1.25} className="mx-auto text-[#C5A059]" />
                      <p className="mt-2 text-[11px] leading-snug text-[#514357]">{t.label}</p>
                    </div>
                  ))}
                </div>
              </motion.div>
            </div>

            {related.length > 0 && (
              <section className="mt-24 md:mt-32" data-testid="related-section">
                <h2 className="font-serif-d font-light text-3xl md:text-4xl text-[#2D1B36] tracking-tight mb-10">
                  You may also <span className="italic text-[#C5A059]">like</span>
                </h2>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-6 md:gap-10">
                  {related.map((p) => (
                    <Link key={p.id} to={`/product/${p.id}`} data-testid={`related-product-${p.id}`} className="group">
                      <div className="relative overflow-hidden bg-[#F2EFEB] aspect-[3/4]">
                        <img
                          src={p.image}
                          alt={p.name}
                          loading="lazy"
                          className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 ease-out group-hover:scale-105"
                        />
                      </div>
                      <h3 className="mt-3 font-serif-d text-lg leading-snug text-[#2D1B36] group-hover:text-[#C5A059] transition-colors">
                        {p.name}
                      </h3>
                      <p className="text-sm font-medium text-[#4A2B58]">{inr(p.price)}</p>
                    </Link>
                  ))}
                </div>
              </section>
            )}
          </>
        )}
      </main>
    </div>
  );
}
